<script setup lang="ts">
    import { DESCRIPTIONS } from '@/constants/descriptions';
    import { SURVEY_APPOINTMENT, SURVEY_ORGANIZER, SURVEY_THEME } from '@/constants/survey';
    import formKitConfig from '@/formkit';
    import { formatCurrencyNoCent } from '@/helpers/formatting';
    import { type EventTypeTyping } from '@/typings/widget';
    import { type FormKitNode } from '@formkit/core';
    import { FormKitProvider, FormKitRoot, defaultConfig } from '@formkit/vue';
    import DatePicker from '@shared/components/form/date-picker.vue';
    import Email from '@shared/components/form/email.vue';
    import PhoneNumber from '@shared/components/form/phone-number.vue';
    import TextInput from '@shared/components/form/text-input.vue';
    import TextArea from '@shared/components/form/textarea.vue';
    import { ConstantsDateTime } from '@shared/constants';
    import { DateTime } from '@shared/helpers';
    import { dayjsObject } from '@shared/helpers/date-time';
    import axios from 'axios';

    const props = defineProps<{
        venue: string
        name: string
        date: string
        locale: string
        eventType: number
        guestAmount?: number
        eventTypes: EventTypeTyping[]
        messageData?: {
            surveyOrganizer: string
            surveyTheme: string
            surveyAppointment: string
            descriptionIds: number[]
            budgetAmount: number
            appointmentDate: string
        }
    }>();

    const emit = defineEmits<{(event: 'processRequest', type: unknown)}>();

    const { t } = useI18n();

    const HANDCUP_IMG = `${import.meta.env.VITE_CDN_URL}/static/illustration-handcup.svg`;

    // Refs
    const formData: Ref<{
        name: string
        surname: string
        eventDate: string
        companyName: string | null
        email: string | null
        phoneNumber: string | null
        message: string
        phoneCountryPrefix: string | null
        guestAmount: number | string | null
        eventTypeId: number | null
    }> = ref({
        name: props.name ?? '',
        surname: '',
        eventDate: props.date ?? '',
        companyName: null,
        email: null,
        phoneNumber: null,
        message: '',
        phoneCountryPrefix: '+31',
        guestAmount: props.guestAmount ?? null,
        eventTypeId: props.eventType ?? null,
    });

    // Computed
    const eventName = computed(() => props.eventTypes.find(event => Number(event.id) === Number(props.eventType))?.name ?? '');
    const organizationType = computed(() => t(SURVEY_ORGANIZER.find(org => org.value === props.messageData?.surveyOrganizer)?.label ?? ''));
    const theme = computed(() => t(SURVEY_THEME.find(thm => thm.value === props.messageData?.surveyTheme)?.label ?? ''));
    const descriptions = computed(() =>
        props.messageData?.descriptionIds
            .map(id => t(DESCRIPTIONS.find(desc => desc.id === id)?.name ?? ''))
            .join(', ') ?? '');
    const budget = computed(() => formatCurrencyNoCent(
        Number(props.messageData?.budgetAmount), 'EUR', props.locale,
    ));
    const appointmentDate = computed(() => props.messageData?.appointmentDate
        ? dayjsObject(props.messageData?.appointmentDate)
            .format('DD MMMM, YYYY')
        : '');

    // Methods
    const getFlexibilityText = (): string => {
        const date = DateTime.dayjsObject(props.date);

        const [
            weekday, day, month, year,
        ] = date.format('dddd D MMMM YYYY')
            .split(' ');

        const surveyAppointmentItems = useEditableClone(SURVEY_APPOINTMENT);
        surveyAppointmentItems[0].label = `${t('widget.survey-appointment.date-only')} ${weekday} ${day} ${month} ${year}`;
        surveyAppointmentItems[1].label = `${t('widget.survey-appointment.date-some-1')} ${weekday} ${t('widget.survey-appointment.date-some-2')} ${month} ${year}`;

        const surveyText = surveyAppointmentItems.find(item => item.value === props.messageData?.surveyAppointment) ?? {
            notranslate: false,
            label: '',
        };

        if (surveyText.notranslate) return surveyText.label;

        return t(surveyText.label);
    };

    const messageBuilder = (): string => {
        if (props.messageData && props.eventType) {
            return `- Event type: ${eventName.value}\n\n- Organization type: ${organizationType.value}\n\n- Theme: ${theme.value}\n\n- Flexibility: ${getFlexibilityText()}\n\n- Descriptions: ${descriptions.value}\n\n- Budget: ${budget.value}\n\n- Appointment Date: ${appointmentDate.value}\n\n`;
        }
        return '';
    };

    // eslint-disable-next-line max-lines-per-function
    const processForm = async(submittedData: typeof formData.value, formNode: FormKitNode): Promise<void> => {
        const {
            name,
            surname,
            eventDate,
            companyName,
            email,
            phoneNumber,
            phoneCountryPrefix,
            guestAmount,
            eventTypeId,
        } = submittedData;

        const payload = {
            eventStartDate: eventDate,
            event: {
                name: `Widget Host Request - ${eventName.value}`,
                mainBooker: {
                    email,
                    userName: `${name} ${surname}`,
                    phoneCountryPrefix,
                    phoneNumber,
                    company: { name: companyName },
                },
                eventTypeId: Number(eventTypeId),
            },
            venuePackageId: '',
            message: messageBuilder() + submittedData.message,
            guestAmount: Number(guestAmount),
        };

        try {
            const response = await axios
                .post(
                    `${import.meta.env.VITE_API_BASE_URL}/api/v1/public/quote/create`, payload, {
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': props.locale,
                            'X-Venue-Id': props.venue,
                        },
                    },
                );
            emit('processRequest', response.data);
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Posting request form failed: ', error);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            const msg = t('widget.messages.errors.request-failed');
            formNode.setErrors([msg]);
        }
    };
</script>

<template>
    <div class="results-form">
        <div class="results-form__info">
            <span>
                {{ t('widget.results.form.desc') }}
            </span>
            <img :src="HANDCUP_IMG">
        </div>
        <div class="results-form__form">
            <h2>
                {{ t('widget.results.form.title') }}
            </h2>
            <span>
                {{ t('widget.results.form.desc') }}
            </span>

            <FormKitProvider :config="defaultConfig(formKitConfig)">
                <FormKitRoot>
                    <FormKit
                        id="results-form"
                        v-model="formData"
                        :incomplete-message="false"
                        :submit-label="t('widget.buttons.send')"
                        :submit-attrs="{
                            inputClass: 'btn btn--primary btn--thin',
                            wrapperClass: 'im-on-the-wrapper',
                            outerClass: 'book-it-btn'
                        }"
                        type="form"
                        @submit="processForm"
                    >
                        <div class="results-form__form__content">
                            <div class="results-form__form__content__row">
                                <TextInput
                                    id="name"
                                    :placeholder="`${t('widget.form.your-name')}*`"
                                    :validation-messages="{
                                        required: `${t('widget.form.validations.required', { field: t('widget.form.your-name') })}`
                                    }"
                                    name="name"
                                    validation="required"
                                />
                                <TextInput
                                    id="surname"
                                    :placeholder="`${t('widget.form.your-surname')}*`"
                                    :validation-messages="{
                                        required: `${t('widget.form.validations.required', { field: t('widget.form.your-surname') })}`
                                    }"
                                    name="surname"
                                    validation="required"
                                />
                            </div>
                            <DatePicker
                                id="eventDate"
                                :placeholder="t('widget.form.event-date')"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.event-date') })}`
                                }"
                                :min-date="
                                    DateTime.dayjsObject().format(ConstantsDateTime.dateFormatCalendar)
                                "
                                name="eventDate"
                                validation="required"
                            />
                            <TextInput
                                id="companyName"
                                :placeholder="t('widget.form.company-name')"
                                name="companyName"
                            />
                            <Email
                                id="email"
                                :placeholder="`${t('widget.form.email')}*`"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.email') })}`
                                }"
                                name="email"
                                validation="required"
                            />
                            <PhoneNumber
                                id="phoneNumber"
                                :validation-messages="{
                                    required: `${t('widget.form.validations.required', { field: t('widget.form.phone-number') })}`
                                }"
                                name="phoneNumber"
                                prefix-name="phoneCountryPrefix"
                                validation="required"
                                placeholder="0 000 000 00*"
                            />
                            <TextArea
                                id="message"
                                :placeholder="t('widget.form.description')"
                                name="message"
                                auto-height
                            />
                        </div>
                    </FormKit>
                </FormKitRoot>
            </FormKitProvider>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.results-form {
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-05);
    padding-inline: var(--spacing-03);

    @include min-width(md) {
        padding-inline: var(--spacing-11);
    }

    &__info {
        display: flex;
        flex: 1;
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
        gap: var(--spacing-05);
        margin-block-start: var(--spacing-10);

        @include max-width(md) {
            display: none;
        }

        img {
            max-block-size: 300px;
        }

        span {
            font-size: var(--font-regular);
        }
    }

    &__form {
        display: flex;
        flex: 1.5;
        flex-direction: column;
        gap: var(--spacing-03);

        @include min-width(md) {
            gap: var(--spacing-05);
        }

        h2 {
            font-size: var(--font-h2);
            text-align: center;
        }

        span {
            display: block;
            font-size: var(--font-regular);
            text-align: center;

            @include min-width(md) {
                display: none;
            }
        }

        &__content {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-03);

            &__row {
                display: flex;
                justify-content: space-between;
                gap: var(--spacing-03);

                > * {
                    flex: 1;
                }
            }
        }

        :deep(.formkit-actions) {
            .book-it-btn {
                display: flex;
                justify-content: flex-end;
                margin-block-start: var(--spacing-03);

                .formkit-wrapper {
                    @include max-width(md) {
                        inline-size: 100%;
                    }
                }

                button {
                    color: var(--text-primary-invert);
                    font-size: var(--font-medium);
                    font-weight: var(--font-weight-medium);
                    inline-size: 100%;
                    letter-spacing: var(--letter-spacing-0);
                    line-height: var(--line-height-150);

                    @include min-width(md) {
                        inline-size: auto;
                    }
                }

            }
        }
    }
}
</style>
